/**
 * WebRTC CDN播放器工具类
 * 用于大班课场景下，使用 TCPlayer 播放 CDN 流
 */

import { uploadLiveLog } from "@saas/components/src/utils/log";
class WebRTCPlayer {
  constructor(options = {}) {
    this.container = options.container;
    this.url = options.url;
    this.onError = options.onError; // 错误回调
    this.onPlaying = options.onPlaying; // 播放回调
    this.onNeedUserInteraction = options.onNeedUserInteraction; // 需要用户交互回调
    this.player = null;
    this.videoElement = null;
    this.playerId =
      "webrtc-player-" +
      Date.now() +
      "-" +
      Math.random().toString(36).substr(2, 9);
    this.isPlaying = false;
    // 性能追踪
    this.initStartTime = null;
    this.performanceData = {};
    // blocked 事件恢复状态追踪
    this.blockedRecoveryTimer = null; // blocked 恢复检测定时器
    this.blockedTriggered = false; // 标记 blocked 是否触发过
  }

  /**
   * 记录关键性能节点并上传日志
   * @param {string} stage 阶段名称
   * @param {object} extraData 额外数据
   */
  logPerformanceStage(stage, extraData = {}) {
    const now = performance.now();
    const elapsed = this.initStartTime
      ? Number((now - this.initStartTime).toFixed(2))
      : 0;
    this.performanceData[stage] = elapsed;

    console.log(`[WebRTCPlayer][性能] ${stage}: +${elapsed}ms`);

    uploadLiveLog({
      message: `CDN播放器-${stage}`,
      level: "info",
      tag: "cdn_player_performance",
      extra: {
        actioin: "startCDNPlayer",
        component: "WebRTCPlayer",
        stage: stage,
        elapsed: elapsed,
        url: this.url,
        allStages: this.performanceData,
        ...extraData,
      },
    });
  }

  /**
   * 初始化播放器
   * @returns {Promise}
   */
  async init() {
    return new Promise((resolve, reject) => {
      try {
        // 关键节点1：初始化开始
        this.initStartTime = performance.now();
        this.logPerformanceStage("1_初始化开始");

        if (!this.container) {
          reject(new Error("容器元素不存在"));
          return;
        }

        // 检查是否已存在 TCPlayer
        if (window.TCPlayer) {
          this.useTCPlayer(resolve, reject);
        } else {
          // 使用原生 video 标签
          this.useNativeVideo(resolve, reject);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 使用腾讯云 TCPlayer
   */
  useTCPlayer(resolve, reject) {
    try {
      // 清空容器
      this.container.innerHTML = "";

      // 创建 video 元素
      this.videoElement = document.createElement("video");
      this.videoElement.id = this.playerId;
      this.videoElement.style.width = "100%";
      this.videoElement.style.height = "100%";
      this.videoElement.style.objectFit = "contain";
      this.videoElement.setAttribute("playsinline", "true");
      this.videoElement.setAttribute("webkit-playsinline", "true");
      this.videoElement.setAttribute("x5-playsinline", "true");
      // 保存 pause 事件处理函数引用，以便后续移除
      this.handlePause = () => {
        console.log("[WebRTCPlayer] 原生video pause");
        this.videoElement.play();
        uploadLiveLog({
          message: "CDN播放器-pause",
          level: "warn",
          tag: "cdn_player_pause",
          extra: {
            actioin: "pause",
            component: "WebRTCPlayer",
          },
        });
      };
      this.videoElement.addEventListener("pause", this.handlePause);
      // 添加到容器
      this.container.appendChild(this.videoElement);

      // 关键节点2：TCPlayer SDK 初始化
      this.player = window.TCPlayer(this.playerId, {
        sources: [
          {
            src: this.url,
          },
        ],
        autoplay: true,
        preload: "auto",
        playbackRates: [1],
        controls: false,
        width: "100%",
        height: "100%",
        live: true,
        h5_flv: false,
        x5_player: true,
        x5_type: "h5",
        x5_fullscreen: false,
        wording: {
          2032: "请求视频失败，请检查网络",
          2048: "请求m3u8文件失败，可能是网络错误或者跨域问题",
        },
      });
      this.logPerformanceStage("2_SDK初始化完成");
      this.player.on("webrtcevent", (event) => {
        console.log("[WebRTCPlayer] TCPlayer webrtcevent:", event);
        uploadLiveLog({
          message: "CDN播放器-webrtcevent",
          level: "log",
          tag: "cdn_player_webrtcevent",
          extra: {
            actioin: "webrtcevent",
            error: JSON.stringify(event?.data),
            component: "WebRTCPlayer",
            url: this.url,
          },
        });
      });
      this.player.on("blocked", (event) => {
        console.warn("[WebRTCPlayer] TCPlayer blocked - 等待自动播放或用户交互:", event);
        this.blockedTriggered = true;

        // 清除之前的定时器，避免重复触发
        if (this.blockedRecoveryTimer) {
          clearTimeout(this.blockedRecoveryTimer);
          this.blockedRecoveryTimer = null;
        }

        // 1.5秒后检查是否已经开始播放
        this.blockedRecoveryTimer = setTimeout(() => {
          // 再次检查播放状态（可能在这1.5秒内已经开始播放了）
          let isActuallyPlaying = false;
          try {
            if (this.player && typeof this.player.paused === 'function') {
              const paused = this.player.paused();
              isActuallyPlaying = !paused;
            } else if (this.videoElement) {
              isActuallyPlaying = !this.videoElement.paused && !this.videoElement.ended;
            }
          } catch (e) {
            console.warn("[WebRTCPlayer] 检查播放状态失败:", e);
          }

          // 如果还没有开始播放，通知上层显示播放按钮
          if (!this.isPlaying && !isActuallyPlaying) {
            console.log("[WebRTCPlayer] blocked 后1.5秒未自动播放，需要用户交互");
            
            if (this.onNeedUserInteraction && typeof this.onNeedUserInteraction === 'function') {
              this.onNeedUserInteraction();
            }

            uploadLiveLog({
              message: "CDN播放器-blocked-需要用户交互",
              level: "warn",
              tag: "cdn_player_blocked",
              extra: {
                actioin: "blocked_need_interaction",
                error: JSON.stringify(event?.data),
                component: "WebRTCPlayer",
                url: this.url,
                isPlaying: this.isPlaying,
                isActuallyPlaying: isActuallyPlaying,
              },
            });
          } else {
            console.log("[WebRTCPlayer] blocked 后1.5秒已自动播放，无需用户交互");
            uploadLiveLog({
              message: "CDN播放器-blocked-已自动恢复",
              level: "info",
              tag: "cdn_player_blocked",
              extra: {
                actioin: "blocked_auto_recovered",
                error: JSON.stringify(event?.data),
                component: "WebRTCPlayer",
                url: this.url,
              },
            });
          }
          this.blockedRecoveryTimer = null;
        }, 1500);

        uploadLiveLog({
          message: "CDN播放器-blocked-事件触发",
          level: "warn",
          tag: "cdn_player_blocked",
          extra: {
            actioin: "blocked",
            error: JSON.stringify(event?.data),
            component: "WebRTCPlayer",
            url: this.url,
          },
        });
      })
      // 监听播放器事件
      this.player.on("error", (error) => {
        console.error("[WebRTCPlayer] TCPlayer 播放错误:", error);

        // 处理错误提示
        const errorCode = error.code || (error.data && error.data.code);
        // const errorMsg = ERROR_MESSAGES[errorCode] || '播放出错，请刷新重试';
        const errorMsg = "网络不稳定，建议检查网络设置或刷新直播间";
        if (this.onError && typeof this.onError === "function") {
          this.onError(errorMsg, error);
        }

        uploadLiveLog({
          message: "CDN播放器-播放错误",
          level: "error",
          tag: "cdn_player_performance",
          extra: {
            actioin: "startCDNPlayer",
            component: "WebRTCPlayer",
            errorCode: errorCode,
            errorMsg: errorMsg,
            error: error?.message || String(error),
            url: this.url,
            allStages: this.performanceData,
          },
        });
      });

      // 关键节点3：开始加载流
      this.player.on("loadstart", () => {
        this.logPerformanceStage("3_开始加载流");
      });

      // 关键节点4：元数据加载完成（CDN响应）
      this.player.on("loadedmetadata", () => {
        this.logPerformanceStage("4_元数据加载完成");
      });

      // 关键节点5：开始播放
      this.player.on("playing", () => {
        this.isPlaying = true;

        // 如果是 blocked 后的播放，清理定时器
        if (this.blockedRecoveryTimer) {
          clearTimeout(this.blockedRecoveryTimer);
          this.blockedRecoveryTimer = null;
        }

        if (this.onPlaying && typeof this.onPlaying === 'function') {
          this.onPlaying();
        }
        this.logPerformanceStage("5_开始播放", {
          // 计算各阶段耗时
          sdkInitTime: this.performanceData["2_SDK初始化完成"] || 0,
          loadStartTime:
            (this.performanceData["3_开始加载流"] || 0) -
            (this.performanceData["2_SDK初始化完成"] || 0),
          metadataLoadTime:
            (this.performanceData["4_元数据加载完成"] || 0) -
            (this.performanceData["3_开始加载流"] || 0),
          playStartTime:
            (this.performanceData["5_开始播放"] || 0) -
            (this.performanceData["4_元数据加载完成"] || 0),
        });
      });

      // 监听等待/卡顿事件（可能影响播放体验）
      this.player.on("waiting", () => {
        console.log("[WebRTCPlayer] TCPlayer waiting - 等待数据");
      });

      this.player.on("stalled", () => {
        console.log("[WebRTCPlayer] TCPlayer stalled - 数据停滞");
      });

      resolve();
    } catch (error) {
      console.error("[WebRTCPlayer] TCPlayer 初始化失败:", error);
      reject(error);
    }
  }

  /**
   * 使用原生 video 标签
   */
  useNativeVideo(resolve, reject) {
    try {
      // 清空容器
      this.container.innerHTML = "";

      // 创建 video 元素
      this.videoElement = document.createElement("video");
      this.videoElement.style.width = "100%";
      this.videoElement.style.height = "100%";
      this.videoElement.style.objectFit = "cover";
      this.videoElement.autoplay = true;
      this.videoElement.muted = false;
      this.videoElement.playsInline = true;

      // 设置视频源
      this.videoElement.src = this.url;

      // 添加到容器
      this.container.appendChild(this.videoElement);

      // 监听播放事件
      this.videoElement.addEventListener("playing", () => {
        console.log("[WebRTCPlayer] 原生video开始播放");
        this.isPlaying = true;
        if (this.onPlaying && typeof this.onPlaying === "function") {
          this.onPlaying();
        }
      });

      this.videoElement.addEventListener("error", (error) => {
        console.error("[WebRTCPlayer] 原生video播放错误:", error);

        if (this.onError && typeof this.onError === "function") {
          this.onError("播放出错，请刷新重试", error);
        }
      });

      // 尝试播放
      this.videoElement
        .play()
        .then(() => {
          resolve();
        })
        .catch((error) => {
          console.error("[WebRTCPlayer] 播放失败:", error);
          reject(error);
        });
    } catch (error) {
      console.error("[WebRTCPlayer] 原生video初始化失败:", error);
      reject(error);
    }
  }

  /**
   * 播放
   * @returns {Promise}
   */
  async play() {
    return new Promise((resolve, reject) => {
      try {
        if (this.player && this.player.play) {
          this.player.play();
          resolve();
        } else if (this.videoElement) {
          this.videoElement
            .play()
            .then(() => {
              this.isPlaying = true;
              resolve();
            })
            .catch(reject);
        } else {
          reject(new Error("播放器未初始化"));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 暂停
   * @returns {Promise}
   */
  async pause() {
    return new Promise((resolve, reject) => {
      try {
        if (this.player && this.player.pause) {
          this.player.pause();
          this.isPlaying = false;
          resolve();
        } else if (this.videoElement) {
          this.videoElement.pause();
          this.isPlaying = false;
          resolve();
        } else {
          reject(new Error("播放器未初始化"));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 停止播放
   * @returns {Promise}
   */
  async stop() {
    return new Promise((resolve) => {
      try {
        if (this.player && this.player.pause) {
          this.player.pause();
        } else if (this.videoElement) {
          this.videoElement.pause();
          this.videoElement.src = "";
        }
        this.isPlaying = false;
        resolve();
      } catch (error) {
        console.error("[WebRTCPlayer] 停止播放失败:", error);
        resolve();
      }
    });
  }

  /**
   * 销毁播放器
   */
  destroy() {
    try {
      // 先暂停播放
      this.isPlaying = false;

      // 清理 blocked 恢复定时器
      if (this.blockedRecoveryTimer) {
        clearTimeout(this.blockedRecoveryTimer);
        this.blockedRecoveryTimer = null;
      }

      // 重置 blocked 恢复状态
      this.blockedTriggered = false;

      if (this.player) {
        // TCPlayer 使用 dispose 方法销毁
        try {
          if (typeof this.player.dispose === "function") {
            this.player.dispose();
          } else if (typeof this.player.destroy === "function") {
            this.player.destroy();
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 销毁player实例失败:", e);
        }
        this.player = null;
      }

      if (this.videoElement) {
        // 停止播放并清除所有资源
        try {
          this.videoElement.pause();
          this.videoElement.removeAttribute("src");
          this.videoElement.load(); // 重新加载以释放资源
          // 移除所有事件监听器
          if (this.handlePause) {
            this.videoElement.removeEventListener("pause", this.handlePause);
            this.handlePause = null;
          }
          this.videoElement.onplaying = null;
          this.videoElement.onerror = null;
          // 从DOM中移除
          if (this.videoElement.parentNode) {
            this.videoElement.parentNode.removeChild(this.videoElement);
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 清理video元素失败:", e);
        }
        this.videoElement = null;
      }

      if (this.container) {
        // 清空容器中的所有内容
        try {
          this.container.innerHTML = "";
        } catch (e) {
          console.error("[WebRTCPlayer] 清空容器失败:", e);
        }
      }

      console.log("[WebRTCPlayer] 播放器已销毁");
    } catch (error) {
      console.error("[WebRTCPlayer] 销毁播放器失败:", error);
    }
  }

  /**
   * 设置音量
   * @param {number} volume 0-1之间的值
   */
  setVolume(volume) {
    try {
      if (this.player) {
        // TCPlayer 使用 volume 方法
        if (typeof this.player.volume === "function") {
          this.player.volume(volume);
        }
      } else if (this.videoElement) {
        this.videoElement.volume = volume;
      }
    } catch (error) {
      console.error("[WebRTCPlayer] 设置音量失败:", error);
    }
  }

  /**
   * 静音/取消静音
   * @param {boolean} muted
   */
  setMuted(muted) {
    try {
      if (this.player) {
        // TCPlayer 使用 muted 方法
        if (typeof this.player.muted === "function") {
          this.player.muted(muted);
        }
      } else if (this.videoElement) {
        this.videoElement.muted = muted;
      }

      // 触发静音状态回调
      if (this.onMuteChange && typeof this.onMuteChange === "function") {
        this.onMuteChange(muted);
      }
    } catch (error) {
      console.error("[WebRTCPlayer] 设置静音失败:", error);
    }
  }
}

export default WebRTCPlayer;
